import React, { useCallback, useContext, useRef } from 'react';
import PropTypes from 'prop-types';
import { useField, useForm } from 'react-final-form';
import { useDispatch } from 'react-redux';

import FormNameContext from './FormNameContext';
import {
  defaultFormat,
  getEventValue,
  identityParse,
  isEvent,
  runFieldValidators,
} from './fieldValue';


const META_KEYS = [
  'active', 'dirty', 'error', 'initial', 'invalid', 'pristine', 'submitError',
  'submitFailed', 'submitSucceeded', 'submitting', 'touched', 'valid', 'validating', 'visited',
];

export const buildMeta = (rffMeta, extra) => META_KEYS.reduce(
  (result, key) => ({ ...result, [key]: rffMeta[key] }),
  { warning: undefined, asyncValidating: false, autofilled: false, ...extra },
);

// <Field> with the redux-form API on top of react-final-form's useField:
// - `component` receives `input` and `meta` plus all other props;
// - `validate` may be an array of validators;
// - `normalize(value, previousValue, allValues)` and `parse(value, name)`;
// - `onChange/onBlur/onFocus(event, newValue, previousValue, name)` props are
//   called before the value is changed (event.preventDefault() cancels it);
// - `defaultValue` is passed to the component (it does not set the value);
// - '' is kept as '' (final-form would store undefined).
const Field = ({
  name,
  component: Component,
  validate = undefined,
  normalize = undefined,
  parse = undefined,
  format = undefined,
  onChange = undefined,
  onBlur = undefined,
  onFocus = undefined,
  type = undefined,
  value: valueProp = undefined,
  ...rest
}) => {
  const form = useForm('Field');
  const formName = useContext(FormNameContext);
  const dispatch = useDispatch();

  const latest = useRef(null);
  latest.current = {
    validate,
    normalize,
    parse,
    format,
    onChange,
    onBlur,
    onFocus,
  };

  const fieldValidate = useCallback(
    (value, allValues) => runFieldValidators(latest.current.validate, value, allValues, name),
    [name],
  );

  const fieldFormat = useCallback((value, fieldName) => {
    const { format: currentFormat } = latest.current;
    if (currentFormat === null) {
      return value;
    }
    return currentFormat ? currentFormat(value, fieldName) : defaultFormat(value);
  }, []);

  const { input: rffInput, meta: rffMeta } = useField(name, {
    validate: fieldValidate,
    parse: identityParse,
    format: fieldFormat,
    type: type === 'checkbox' || type === 'radio' ? type : undefined,
    value: valueProp,
  });

  const getPreviousValue = () => {
    const fieldState = form.getFieldState(name);
    return fieldState ? fieldState.value : undefined;
  };

  const handleChange = (eventOrValue) => {
    const {
      normalize: currentNormalize,
      parse: currentParse,
      onChange: currentOnChange,
    } = latest.current;
    const previousValue = getPreviousValue();
    let value;
    if (type === 'radio' && isEvent(eventOrValue)) {
      value = valueProp;
    } else {
      value = getEventValue(eventOrValue);
    }
    if (currentParse) {
      value = currentParse(value, name);
    }
    if (currentNormalize) {
      const allValues = form.getState().values;
      value = currentNormalize(value, previousValue, allValues, allValues);
    }
    if (currentOnChange) {
      currentOnChange(eventOrValue, value, previousValue, name);
      if (isEvent(eventOrValue) && eventOrValue.defaultPrevented) {
        return;
      }
    }
    form.change(name, value);
  };

  const handleBlur = (event) => {
    const { onBlur: currentOnBlur } = latest.current;
    if (currentOnBlur) {
      currentOnBlur(event, getPreviousValue(), getPreviousValue(), name);
    }
    rffInput.onBlur(event);
  };

  const handleFocus = (event) => {
    const { onFocus: currentOnFocus } = latest.current;
    if (currentOnFocus) {
      currentOnFocus(event, name);
    }
    rffInput.onFocus(event);
  };

  const input = {
    name,
    value: type === 'radio' ? valueProp : rffInput.value,
    onChange: handleChange,
    onBlur: handleBlur,
    onFocus: handleFocus,
    ...(type === 'checkbox' || type === 'radio' ? { checked: rffInput.checked } : {}),
  };

  const meta = buildMeta(rffMeta, { form: formName, dispatch });

  if (typeof Component === 'string') {
    return React.createElement(Component, { ...input, type, ...rest });
  }
  return (
    <Component
      {...rest}
      type={type}
      input={input}
      meta={meta}
    />
  );
};

Field.propTypes = {
  name: PropTypes.string.isRequired,
  component: PropTypes.elementType.isRequired,
  validate: PropTypes.oneOfType([PropTypes.func, PropTypes.arrayOf(PropTypes.func)]),
  normalize: PropTypes.func,
  parse: PropTypes.func,
  format: PropTypes.func,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  type: PropTypes.string,
  value: PropTypes.any,
};

export default Field;
